import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, Check, Pencil, Square, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { usePromotionDetail, useEndPromotion, useIssueCouponToGroupNow } from "./api/promotionHooks";
import { PromotionStatusBadge } from "./components/PromotionStatusBadge";
import { PromotionStatsCards } from "./components/PromotionStatsCards";
import { useAuthStore } from "@/lib/store/authStore";
import { safeDecodeURIComponent } from "@/lib/utils";

export function PromotionDetailPage() {
  const { couponCode: rawCode } = useParams<{ couponCode: string }>();
  const couponCode = safeDecodeURIComponent(rawCode || "");
  const navigate = useNavigate();
  const tenant = useAuthStore((s) => s.currentTenant);

  const company = useAuthStore((s) => s.tenantCompany);

  const { data: promotion, isLoading } = usePromotionDetail(couponCode);
  const endMutation = useEndPromotion(couponCode);
  const issueMutation = useIssueCouponToGroupNow(couponCode);

  const [showEndDialog, setShowEndDialog] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const storefrontUrl = tenant && couponCode
    ? `https://${tenant}.shop.zaviago.com/coupons?code=${encodeURIComponent(couponCode)}`
    : "";

  const copyToClipboard = async (text: string, type: "code" | "link") => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    if (type === "code") {
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } else {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const handleEnd = () => {
    endMutation.mutate(undefined, {
      onSuccess: () => setShowEndDialog(false),
    });
  };

  const canEnd = promotion && (promotion.status === "Active" || promotion.status === "Scheduled");

  if (isLoading) {
    return (
      <div className="mx-auto max-w-5xl px-4 pt-12">
        <div className="h-8 w-32 animate-pulse rounded-md bg-gray-100" />
        <div className="mt-6 h-48 animate-pulse rounded-sm bg-gray-100" />
        <div className="mt-6 grid grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-28 animate-pulse rounded-sm bg-gray-100" />
          ))}
        </div>
      </div>
    );
  }

  if (!promotion) {
    return (
      <div className="mx-auto max-w-5xl px-4 pt-12">
        <p className="text-muted-foreground">Promotion not found.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/promotions")}>
          Back to Promotions
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 pt-12 pb-16">
      {/* Back link */}
      <button
        onClick={() => navigate("/promotions")}
        className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Promotions
      </button>

      {/* Summary Card */}
      <div className="rounded-sm border bg-white p-6">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-semibold">{promotion.promotion_title}</h1>
            {promotion.promotion_description && (
              <p className="mt-1 text-sm text-muted-foreground">
                {promotion.promotion_description}
              </p>
            )}
          </div>
          <div className="ml-4 flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                navigate(`/promotions/${encodeURIComponent(couponCode)}/edit`)
              }
            >
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              Edit
            </Button>
            {canEnd && (
              <Button
                variant="outline"
                size="sm"
                className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={() => setShowEndDialog(true)}
              >
                <Square className="mr-1.5 h-3.5 w-3.5" />
                End
              </Button>
            )}
          </div>
        </div>

        {/* Code and link */}
        <div className="mt-4 space-y-2">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Code:</span>
            <span className="font-mono font-medium">{promotion.coupon_code}</span>
            <button
              onClick={() => copyToClipboard(promotion.coupon_code, "code")}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              {codeCopied ? (
                <>
                  <Check className="h-3.5 w-3.5" /> Copied!
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" /> Copy
                </>
              )}
            </button>
          </div>
          {storefrontUrl && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">Link:</span>
              <span className="truncate text-sm">{storefrontUrl}</span>
              <button
                onClick={() => copyToClipboard(storefrontUrl, "link")}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              >
                {linkCopied ? (
                  <>
                    <Check className="h-3.5 w-3.5" /> Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" /> Copy
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Metadata */}
        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
          <span className="text-muted-foreground">
            {promotion.discount_display}
          </span>
          {promotion.minimum_order > 0 && (
            <>
              <span className="text-muted-foreground">|</span>
              <span className="text-muted-foreground">
                Min. order ฿{promotion.minimum_order.toLocaleString()}
              </span>
            </>
          )}
          {promotion.minimum_cart_qty > 0 && (
            <>
              <span className="text-muted-foreground">|</span>
              <span className="text-muted-foreground">
                Min. {promotion.minimum_cart_qty} items
              </span>
            </>
          )}
          <span className="text-muted-foreground">|</span>
          <span
            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
              promotion.is_public
                ? "bg-blue-50 text-blue-700"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {promotion.is_public ? "Public" : "Exclusive"}
          </span>
          <span className="text-muted-foreground">|</span>
          <PromotionStatusBadge status={promotion.status} />
          {promotion.valid_upto && (
            <>
              <span className="text-muted-foreground">|</span>
              <span className="text-muted-foreground">
                {promotion.status === "Ended" ? "Ended" : "Ends"}{" "}
                {new Date(promotion.valid_upto).toLocaleDateString("en-US", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="mt-6">
        <PromotionStatsCards
          stats={promotion.stats}
          maxCollections={promotion.max_collections}
        />
      </div>

      {/* Target Customer Group */}
      {promotion.custom_target_customer_group && (
        <div className="mt-6 rounded-sm border bg-white p-6">
          <h2 className="text-sm font-semibold text-muted-foreground mb-3">
            TARGET CUSTOMER GROUP
          </h2>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">
                {promotion.custom_target_customer_group}
              </p>
              {typeof promotion.target_group_customer_count === "number" && (
                <p className="text-sm text-muted-foreground">
                  {promotion.target_group_customer_count} customer(s) in this group
                </p>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={issueMutation.isPending || !company}
              onClick={() => company && issueMutation.mutate(company)}
            >
              <Send className="mr-1.5 h-3.5 w-3.5" />
              {issueMutation.isPending ? "Issuing..." : "Issue Now"}
            </Button>
          </div>
        </div>
      )}

      {/* End Promotion Dialog */}
      <Dialog open={showEndDialog} onOpenChange={setShowEndDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>End this promotion?</DialogTitle>
            <DialogDescription>
              Customers who collected it won't be able to use it anymore.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEndDialog(false)}
              disabled={endMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleEnd}
              disabled={endMutation.isPending}
            >
              {endMutation.isPending ? "Ending..." : "End Promotion"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
